import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useCreateCandidateV2, useCompanyUsersV2 } from '@/hooks/useCandidatesV2';

const SOURCES = ['manual', 'linkedin', 'referral', 'imported'];
const SOURCE_LABELS = { manual: 'Manual', linkedin: 'LinkedIn', referral: 'Referral', imported: 'Imported' };

const EMPTY = {
  firstName: '', lastName: '', email: '', phone: '', location: '',
  currentTitle: '', currentCompany: '', experienceYears: '',
  source: '', owner: '', tags: '', skills: '', summary: '', resumeUrl: '',
};

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const CreateCandidateModalV2 = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');

  const { data: usersData } = useCompanyUsersV2();
  const users = Array.isArray(usersData) ? usersData : (usersData?.users || []);
  const createMutation = useCreateCandidateV2();

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setErrors((p) => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!isValidEmail(form.email)) e.email = 'Invalid email address';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim() || undefined,
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      location: form.location.trim() || undefined,
      currentTitle: form.currentTitle.trim() || undefined,
      currentCompany: form.currentCompany.trim() || undefined,
      experienceYears: form.experienceYears !== '' ? Number(form.experienceYears) : undefined,
      source: form.source || undefined,
      owner: form.owner || undefined,
      tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      skills: form.skills ? form.skills.split(',').map((s) => s.trim()).filter(Boolean) : [],
      summary: form.summary.trim() || undefined,
      resumeUrl: form.resumeUrl.trim() || undefined,
    };

    createMutation.mutate(payload, {
      onSuccess: () => { setForm(EMPTY); setErrors({}); onClose(); },
      onError: (err) => {
        const msg = err.response?.data?.message;
        if (err.response?.status === 400 && msg) setServerError(msg);
      },
    });
  };

  const handleClose = () => { setForm(EMPTY); setErrors({}); setServerError(''); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Candidate</DialogTitle>
        </DialogHeader>

        {serverError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2">{serverError}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-1">
              <Label>First Name <span className="text-red-500">*</span></Label>
              <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="John" />
              {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
            </div>
            {/* Last Name */}
            <div className="space-y-1">
              <Label>Last Name</Label>
              <Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="Doe" />
            </div>
            {/* Email */}
            <div className="space-y-1">
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="john@example.com" />
              {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
            </div>
            {/* Phone */}
            <div className="space-y-1">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+1 555 000 0000" />
            </div>
            {/* Location */}
            <div className="space-y-1">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="New York, NY" />
            </div>
            {/* Current Title */}
            <div className="space-y-1">
              <Label>Current Title</Label>
              <Input value={form.currentTitle} onChange={(e) => set('currentTitle', e.target.value)} placeholder="Software Engineer" />
            </div>
            {/* Current Company */}
            <div className="space-y-1">
              <Label>Current Company</Label>
              <Input value={form.currentCompany} onChange={(e) => set('currentCompany', e.target.value)} placeholder="Acme Corp" />
            </div>
            {/* Experience Years */}
            <div className="space-y-1">
              <Label>Experience (years)</Label>
              <Input type="number" min="0" max="50" value={form.experienceYears} onChange={(e) => set('experienceYears', e.target.value)} placeholder="5" />
            </div>
            {/* Source */}
            <div className="space-y-1">
              <Label>Source</Label>
              <Select value={form.source || 'none'} onValueChange={(v) => set('source', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{SOURCE_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Owner */}
            <div className="space-y-1">
              <Label>Owner</Label>
              <Select value={form.owner || 'none'} onValueChange={(v) => set('owner', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No owner</SelectItem>
                  {users.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-1">
            <Label>Tags <span className="text-xs text-gray-400">(comma-separated)</span></Label>
            <Input value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="frontend, react, senior" />
          </div>

          {/* Skills */}
          <div className="space-y-1">
            <Label>Skills <span className="text-xs text-gray-400">(comma-separated)</span></Label>
            <Input value={form.skills} onChange={(e) => set('skills', e.target.value)} placeholder="JavaScript, TypeScript, Node.js" />
          </div>

          {/* Summary */}
          <div className="space-y-1">
            <Label>Summary</Label>
            <Textarea value={form.summary} onChange={(e) => set('summary', e.target.value)} rows={3} placeholder="Brief candidate summary..." />
          </div>

          {/* Resume URL */}
          <div className="space-y-1">
            <Label>Resume URL</Label>
            <Input value={form.resumeUrl} onChange={(e) => set('resumeUrl', e.target.value)} placeholder="https://..." />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCandidateModalV2;
